import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { useTheme, styled } from '@mui/material/styles';
import { VariableSizeList } from 'react-window';
import Typography from '@mui/material/Typography';
import React, { createContext, forwardRef, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { CircularProgress } from '@mui/material';
import { API_DEVOLUCION_MERCANCIAS } from '../../services/devoluciones_mercancias';

const LISTBOX_PADDING = 8; // px
let labelsArr =  [];

function renderRow(props) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: style.top + LISTBOX_PADDING,
  };

  const getOptionLabel = (option) => {
    const labelsArray = labelsArr.map(lab => `${option[lab]}`);
    const label = labelsArray.join(" - ");
    return label;
  }

  if (dataSet.hasOwnProperty('group')) {
    return (
      <>
        <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
          {dataSet.group}
        </ListSubheader>
      </>
    );
  }

  return (
    <>
      <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
        {getOptionLabel(dataSet[1])}
      </Typography>
    </>
  );
}

const OuterElementContext = createContext({});

const OuterElementType = forwardRef((props, ref) => {
  const outerProps = useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = forwardRef(function ListboxComponent(props, ref) {
  const { children, ...other } = props;
  const itemData = [];
  children.forEach((item) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
    noSsr: true,
  });

  const itemCount = itemData?.length;
  const itemSize = smUp ? 36 : 48;

  const getChildSize = (child) => {
    if (child.hasOwnProperty('group')) {
      return 48;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

export default function VirtualizedAutocompleteFacturas({
    id,
    control, 
    labels,
    onChange
}) {

    labelsArr = labels; 
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [inputValue, setinputValue] = useState("");
    const [loading, setLoading] = useState(open && options?.length === 0);
    let timeout = null;

    useEffect(() => {
        if (!open) setOptions([]);
    }, [open]);

    useEffect(
        () => {clearTimeOut();}
    );

    useEffect(() => {
  
    }, []);

    const handleOnChange = (value) => { 
      onChange(value); 

      if(!value) { setOpen(false); }
    }

    const getVirtualizeOptionLabel = (option) => {
      const labelsArray = labels.map(lab => `${option[lab]}`);
      return labelsArray.join(' - ');
    }

    const startTimeOut = (e) =>  timeout = setTimeout(() => { 
        timeout = null;
        handleChangeInput(e);
    }, 600);
    const clearTimeOut = () => timeout ? clearTimeout(timeout) : null;

    const handleChangeInput = async(e) => {
        const value = e.target.value;
        await handleSearchOptions(value);
        clearTimeOut();
        setinputValue(value);
    }

    const handleSearchOptions = async(value) => {
        setLoading(true);
        const response = await API_DEVOLUCION_MERCANCIAS.searchVenta(value);
        const data = await response.data;
        
        if(response && response.success) {
            setOptions(data);
            setLoading(false);
        }
    }

    return (
      <Controller
          name={id}
          control={control}
          defaultValue={null}
          render={({ field: props }) => (
            <Autocomplete
                {...props}
                id={id}
                sx={{ width: "100%" }}
                onChange={(e, value)=> handleOnChange(value)}
                disableListWrap
                PopperComponent={StyledPopper}
                ListboxComponent={ListboxComponent}
                options={options}
                getOptionLabel={(option) =>  getVirtualizeOptionLabel(option)}
                renderOption={(props, option) => [props, option]}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={loading}
                open={open && inputValue?.length > 2}
                onOpen={() => { setOpen(true); }}
                onClose={() => { setOpen(false); }}
                onKeyUp={(e) => {
                    e.preventDefault();
                    clearTimeOut();
                    startTimeOut(e);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        placeholder='Introduce Nº factura o Nº presupuesto'
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                            <Fragment>
                                {loading ? <CircularProgress color="inherit" size={15} /> : null}
                                {params.InputProps.endAdornment}
                            </Fragment>
                            ),
                        }}
                    />
                )}
                renderGroup={(params) => params}
            />
          )}
        />
    );
}